import { createTranslator } from "../../i18n/index.js";
import { APP_LANGUAGE_ENGLISH } from "../../lib/settings/appSettings.js";
import { EXPENSE_TYPE_IRREGULAR, EXPENSE_TYPE_ONGOING } from "../../lib/expenseType.js";
import { PROFILE_DATA_EXPORT_VERSION } from "../../features/settings/profileDataExport.js";
import generatorSkeleton from "./generator.json";

function padMonth(value) {
  return String(value).padStart(2, "0");
}

function buildDateString(year, monthIndex, day) {
  return `${year}-${padMonth(monthIndex + 1)}-${padMonth(day)}`;
}

function hashString(value) {
  let hash = 2166136261;

  String(value ?? "").split("").forEach((character) => {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  });

  return hash >>> 0;
}

function createSeededRng(seedText) {
  let state = hashString(seedText) || 1;

  return () => {
    state += 0x6D2B79F5;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function randomBetween(rng, min, max) {
  return min + ((max - min) * rng());
}

function resolveNumericRange(range, fallback) {
  if (!Array.isArray(range) || range.length < 2) {
    return fallback;
  }

  const first = Number(range[0]);
  const second = Number(range[1]);

  if (!Number.isFinite(first) || !Number.isFinite(second)) {
    return fallback;
  }

  return first <= second ? [first, second] : [second, first];
}

function chooseArrayValue(values, rng, fallback = 0) {
  if (!Array.isArray(values) || values.length === 0) {
    return fallback;
  }

  return values[Math.floor(rng() * values.length)] ?? fallback;
}

function pickConfiguredValue(value, rng, fallback = 0) {
  if (Array.isArray(value)) {
    return chooseArrayValue(value, rng, fallback);
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function pickAmount(amounts, rng) {
  if (Array.isArray(amounts)) {
    return Math.round(chooseArrayValue(amounts, rng, 0));
  }

  if (amounts && typeof amounts === "object") {
    const min = Number(amounts.min);
    const max = Number(amounts.max);

    if (Number.isFinite(min) && Number.isFinite(max)) {
      return Math.round(randomBetween(rng, Math.min(min, max), Math.max(min, max)));
    }
  }

  const numericAmount = Number(amounts);
  return Number.isFinite(numericAmount) ? Math.round(numericAmount) : 0;
}

function buildLocalizedName(t, namespace, key) {
  return t(`${namespace}.${key}`);
}

const paymentMethodNamespace = generatorSkeleton.localization?.paymentMethodNamespace ?? "generator.paymentMethod";
const configuredPaymentMethods = Array.isArray(generatorSkeleton.paymentMethods)
  ? generatorSkeleton.paymentMethods
  : [];
const normalizedPaymentMethods = configuredPaymentMethods
  .map((entry) => {
    const key = String(entry?.key ?? "").trim();
    const weight = Number.isFinite(Number(entry?.weight ?? 1)) ? Number(entry.weight ?? 1) : 1;
    return { key, weight };
  })
  .filter((entry) => entry.key);
const paymentMethodPool = normalizedPaymentMethods.length > 0
  ? normalizedPaymentMethods
  : [{ key: "card", weight: 1 }];

function choosePaymentMethodKey(rng) {
  if (!Array.isArray(paymentMethodPool) || paymentMethodPool.length === 0) {
    return "card";
  }

  const totalWeight = paymentMethodPool.reduce((sum, entry) => sum + Math.max(0, entry.weight), 0);

  if (totalWeight > 0) {
    let target = rng() * totalWeight;
    for (const entry of paymentMethodPool) {
      const weight = Math.max(0, entry.weight);
      if (target < weight) {
        return entry.key;
      }
      target -= weight;
    }
  }

  return paymentMethodPool[Math.floor(rng() * paymentMethodPool.length)].key;
}

function resolvePaymentMethodLabel(t, rng) {
  const paymentMethodKey = choosePaymentMethodKey(rng);
  return buildLocalizedName(t, paymentMethodNamespace, paymentMethodKey);
}

function resolveSupplierGroups(category) {
  const expensePattern = category.expensePattern ?? {};

  return {
    ongoing: Array.isArray(expensePattern.ongoingSuppliers)
      ? expensePattern.ongoingSuppliers
      : (Array.isArray(expensePattern.suppliers) ? expensePattern.suppliers : []),
    irregular: Array.isArray(expensePattern.irregularSuppliers)
      ? expensePattern.irregularSuppliers
      : []
  };
}

function buildBudgetRows(categories, t, rng) {
  return categories.map((category) => {
    const categoryName = buildLocalizedName(t, generatorSkeleton.localization.categoryNamespace, category.key);

    return {
      id: `budget-${category.key}`,
      category: categoryName,
      ongoingBudget: pickConfiguredValue(category.budget?.ongoing, rng, 0),
      irregularBudget: pickConfiguredValue(category.budget?.irregular, rng, 0)
    };
  });
}

function buildExpenseRow({
  amount,
  categoryKey,
  categoryName,
  monthIndex,
  supplierName,
  transactionIndex,
  type,
  year,
  methodLabel
}) {
  const day = Math.min(28, 5 + (transactionIndex * 9));

  return {
    id: `expense-${type}-${categoryKey}-${year}-${padMonth(monthIndex + 1)}-${transactionIndex + 1}`,
    date: buildDateString(year, monthIndex, day),
    amount,
    method: methodLabel ?? "Card",
    type,
    supplier: supplierName,
    categoryId: `budget-${categoryKey}`,
    category: categoryName,
    service: ""
  };
}

function buildMonthlyExpenseRows({
  baseBudget,
  category,
  categoryIndex,
  categoryName,
  monthCount,
  rng,
  suppliers,
  targetMultiplierRange,
  type,
  t,
  year
}) {
  const rows = [];

  if (baseBudget <= 0 || suppliers.length === 0) {
    return rows;
  }

  for (let monthIndex = 0; monthIndex < monthCount; monthIndex += 1) {
    const monthlyTarget = Math.max(
      0,
      Math.round(baseBudget * randomBetween(rng, targetMultiplierRange[0], targetMultiplierRange[1]))
    );

    if (monthlyTarget <= 0) {
      continue;
    }

    let spent = 0;
    let transactionIndex = 0;
    let supplierIndex = monthIndex + categoryIndex;

    while (transactionIndex < suppliers.length * 4) {
      const supplier = suppliers[supplierIndex % suppliers.length];
      const supplierName = buildLocalizedName(t, generatorSkeleton.localization.supplierNamespace, supplier.key);
      const amount = pickAmount(supplier.amounts, rng);
      const paymentMethodLabel = resolvePaymentMethodLabel(t, rng);

      if (amount <= 0 || spent + amount > monthlyTarget) {
        break;
      }

      rows.push(buildExpenseRow({
        amount,
        categoryKey: category.key,
        categoryName,
        monthIndex,
        supplierName,
        transactionIndex,
        type,
        year,
        methodLabel: paymentMethodLabel
      }));

      spent += amount;
      transactionIndex += 1;
      supplierIndex += 1;
    }
  }

  return rows;
}

function buildIrregularExpenseRows({
  baseBudget,
  category,
  categoryIndex,
  categoryName,
  monthCount,
  rng,
  suppliers,
  targetMultiplierRange,
  t,
  year
}) {
  const rows = [];

  if (baseBudget <= 0 || suppliers.length === 0) {
    return rows;
  }

  let remainingBudget = baseBudget;

  for (let monthIndex = 0; monthIndex < monthCount; monthIndex += 1) {
    if (remainingBudget <= 0) {
      break;
    }

    const monthlyTarget = Math.max(
      0,
      Math.round(remainingBudget * randomBetween(rng, targetMultiplierRange[0], targetMultiplierRange[1]))
    );

    if (monthlyTarget <= 0) {
      continue;
    }

    let spent = 0;
    let transactionIndex = 0;
    let supplierIndex = monthIndex + categoryIndex;

    while (transactionIndex < suppliers.length * 4) {
      const supplier = suppliers[supplierIndex % suppliers.length];
      const supplierName = buildLocalizedName(t, generatorSkeleton.localization.supplierNamespace, supplier.key);
      const amount = pickAmount(supplier.amounts, rng);
      const paymentMethodLabel = resolvePaymentMethodLabel(t, rng);

      if (amount <= 0 || spent + amount > monthlyTarget) {
        break;
      }

      rows.push(buildExpenseRow({
        amount,
        categoryKey: category.key,
        categoryName,
        monthIndex,
        supplierName,
        transactionIndex,
        type: EXPENSE_TYPE_IRREGULAR,
        year,
        methodLabel: paymentMethodLabel
      }));

      spent += amount;
      transactionIndex += 1;
      supplierIndex += 1;
    }

    remainingBudget = Math.max(0, remainingBudget - spent);
  }

  return rows;
}

function buildExpenseRows(categories, t, now, rng) {
  const year = String(now.getFullYear());
  const monthCount = now.getMonth() + 1;
  const ongoingTargetMultiplierRange = resolveNumericRange(generatorSkeleton.generation?.ongoingTargetMultiplier, [0.9, 1.1]);
  const irregularTargetMultiplierRange = resolveNumericRange(generatorSkeleton.generation?.irregularTargetMultiplier, [0, 0.5]);

  const rows = categories.flatMap((category, categoryIndex) => {
    const categoryName = buildLocalizedName(t, generatorSkeleton.localization.categoryNamespace, category.key);
    const suppliers = resolveSupplierGroups(category);
    const ongoingBudget = pickConfiguredValue(category.budget?.ongoing, rng, 0);
    const irregularBudget = pickConfiguredValue(category.budget?.irregular, rng, 0);

    return [
      ...buildMonthlyExpenseRows({
        baseBudget: ongoingBudget,
        category,
        categoryIndex,
        categoryName,
        monthCount,
        rng,
        suppliers: suppliers.ongoing,
        targetMultiplierRange: ongoingTargetMultiplierRange,
        t,
        type: EXPENSE_TYPE_ONGOING,
        year
      }),
      ...buildIrregularExpenseRows({
        baseBudget: irregularBudget,
        category,
        categoryIndex,
        categoryName,
        monthCount,
        rng,
        suppliers: suppliers.irregular,
        targetMultiplierRange: irregularTargetMultiplierRange,
        t,
        year
      })
    ];
  });

  return rows.sort((left, right) => {
    const dateComparison = String(left.date ?? "").localeCompare(String(right.date ?? ""));
    if (dateComparison !== 0) {
      return dateComparison;
    }

    return String(left.id ?? "").localeCompare(String(right.id ?? ""));
  });
}

export function buildSampleProfileDataExport({
  language = APP_LANGUAGE_ENGLISH,
  now = new Date()
} = {}) {
  const t = createTranslator(language);
  const year = String(now.getFullYear());
  const rng = createSeededRng(`${language}:${year}:${generatorSkeleton.version}`);
  const budgetRows = buildBudgetRows(generatorSkeleton.categories, t, rng);
  const expenseRows = buildExpenseRows(generatorSkeleton.categories, t, now, rng);

  return {
    version: PROFILE_DATA_EXPORT_VERSION,
    exportedAt: now.toISOString(),
    activeYear: year,
    profile: {
      name: t("generator.profileName"),
      source: "import"
    },
    years: [
      {
        year,
        archivedAt: null
      }
    ],
    budgets: [
      {
        year,
        rows: budgetRows
      }
    ],
    expenses: [
      {
        year,
        rows: expenseRows
      }
    ]
  };
}
